/** @jsx jsx */
import { useState, useEffect, useMemo } from 'react'
import { useRouter } from 'next/router'
import { PageLayout } from 'components/PageLayout'
import { Mapbox } from 'components/mapbox/Mapbox'
import { jsx, Flex, Text, useThemeUI, useColorMode } from 'theme-ui'
import { useGlobalStaticData } from 'hooks/useGlobalStaticData'
import { ttLocationsQuery, vantagePointsQuery } from 'lib/sanity/queries/map'
import { sanityClient, getClient } from 'lib/sanity/sanity.server'
import { usePreviewSubscription } from 'lib/sanity/sanity'
import Link from 'next/link'
import { useResponsiveValue } from '@theme-ui/match-media'

const ddtSlugs = [
  '3-2',
  'double-eclipse',
  'earth-deities',
  'behind-the-sun',
  'the-character-ride',
  'slow-room',
  'o-peixe',
  'the-purging',
  'home-state-nipaluna',
]

const vpSlugs = [
  'constitution-dock-b',
  'princes-park',
  'brooke-street-pier',
  'constitution-dock-a',
  'macquarie-wharf-no-1',
  'cenotaph',
  'victoria-esplanade',
  'bellerive-beach',
  'rosny-point',
]

export default function MapPage({ data = {}, preview }) {
  const router = useRouter()
  const context = useThemeUI()
  const [colorMode] = useColorMode()

  const red =
    colorMode === 'dark'
      ? context.theme.rawColors.red
      : context.theme.rawColors.modes.light.red

  const iconPrefix = colorMode === 'dark' ? '' : 'bw-'

  const { slug: slugSplit } = router.query
  const slug = Array.isArray(slugSplit) ? slugSplit[0] : slugSplit //TODO: fix this here tomorrow

  const {
    home: { events },
    vantagePoints,
  } = useGlobalStaticData()

  const {
    data: { ttLocations },
  } = usePreviewSubscription(ttLocationsQuery, {
    initialData: data,
    enabled: preview,
  })

  const darkMapLocations = useMemo(
    () =>
      events
        // Filter out events that dont have a location
        .filter(event => {
          return event.locations
        })
        // return a flat array of all the locations
        .flatMap(event => {
          return (
            event.locations
              // filter out locations that should be hidden on the map
              .filter(eventLocation => {
                return !eventLocation.hideOnMap
              })
          )
        })
        // filter out duplicates
        .filter((arrLoc, i, arr) => {
          return arr.findIndex(loc => loc.slug === arrLoc.slug) === i
        }),
    [events],
  )

  const [mapState, setMapState] = useState(() => {
    return ttLocations.filter(loc => loc.slug === slug)[0] ||
      slug === 'off-season'
      ? 'off-season'
      : 'dark-mofo'
  })

  const locationForSlug =
    mapState === 'dark-mofo'
      ? darkMapLocations.filter(loc => loc.slug === slug)[0]
      : ttLocations.filter(loc => loc.slug === slug)[0]

  const metaFields = {
    metaTitle: locationForSlug?.name ?? 'Map',
  }

  // zoom settings
  const waterfrontZoom = useResponsiveValue(() => [12, 12, 13, 13.5])
  const ddtZoom = useResponsiveValue(() => [15.5, 16.4, 16.6])
  const darkMapZoom = useResponsiveValue(() => [13.1, 13.1, 14.3, 14])
  const ttZoom = useResponsiveValue(() => [5.7, 5.7, 6.5, 7])

  return (
    <PageLayout
      preview={preview}
      metaFields={metaFields}
      isCorrectSlug={true} // Must assert is true for metaFields to be respected
      containerVariant={'mapContainer'}
      mapState={mapState}
      setMapState={setMapState}
      sx={{
        pb: [0, 0, 0, 0, 0],
      }}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          height: '100%',
        }}
        pt={[2]}
        pb={[0, 0]}
      >
        <Mapbox locationToCentre={locationForSlug}>
          {mapState === 'dark-mofo' ? (
            <Mapbox.Layer
              layerName="darkdowntown"
              visible={true}
              pointer={slug !== 'dark-downtown'}
              onClick={(e, map) => {
                // prevent bubbling and make sure the path doesnt include dark-downtown
                if (
                  e.originalEvent.target === map.getCanvas() &&
                  !router.asPath.includes('dark-downtown')
                ) {
                  router.push(`/map/dark-downtown`, undefined, {
                    shallow: true,
                  })
                }
              }}
            >
              {/* Hobart Waterfront */}
              <Mapbox.Layer
                layerName="launch-title"
                visible={slug === 'hobart-waterfront'}
              />
              <Mapbox.Layer
                layerName="launch-line"
                visible={slug === 'hobart-waterfront'}
              />
              <Mapbox.Layer
                layerName="vantage-points"
                visible={slug === 'hobart-waterfront'}
                pointer={true}
                onInit={map => {
                  if (Array.isArray(slugSplit) && slugSplit.length > 1) {
                    map.setLayoutProperty('vantage-points', 'icon-image', [
                      'match',
                      ['get', 'slug'],
                      slugSplit[1],
                      `${iconPrefix}active`,
                      `${iconPrefix}vantage`,
                    ])
                  }
                }}
                onClick={(e, map) => {
                  const feature = e.features[0]

                  router
                    .push(
                      `/map/hobart-waterfront/${feature.properties.slug}`,
                      undefined,
                      { shallow: true },
                    )
                    .then(() => {
                      map.setLayoutProperty('vantage-points', 'icon-image', [
                        'match',
                        ['get', 'slug'],
                        feature.properties.slug,
                        `${iconPrefix}active`,
                        `${iconPrefix}vantage`,
                      ])
                    })
                }}
              />
              {slug === 'hobart-waterfront' && (
                <Mapbox.Zoom
                  zoom={waterfrontZoom}
                  coords={['147.35005575710136', '-42.88266738410769']}
                >
                  <BackToMap />
                  {slugSplit.length > 1 && (
                    <Mapbox.Modal
                      location={
                        vantagePoints.filter(
                          point => point.slug === slugSplit[1],
                        )[0]
                      }
                      onClose={map => {
                        router.push(`/map/hobart-waterfront`, undefined, {
                          shallow: true,
                        })
                        map.setLayoutProperty(
                          'vantage-points',
                          'icon-image',
                          `${iconPrefix}vantage`,
                        )
                      }}
                    />
                  )}
                  {slugSplit.length < 2 && (
                    <Mapbox.Modal vantagePoints={true} onClose={() => {}} />
                  )}
                </Mapbox.Zoom>
              )}
              {/* Hobart Waterfront END */}
              {/* Dark Downtown */}
              <Mapbox.Layer
                layerName="artworks"
                visible={slug === 'dark-downtown'}
                onInit={map => {
                  if (Array.isArray(slugSplit) && slugSplit.length > 1) {
                    map.setPaintProperty('artworks', 'text-opacity', [
                      'match',
                      ['get', 'slug'],
                      slugSplit[1],
                      0,
                      1,
                    ])

                    map.setLayoutProperty('artworks', 'icon-image', [
                      'match',
                      ['get', 'slug'],
                      slugSplit[1],
                      `${iconPrefix}active`,
                      `${iconPrefix}base`,
                    ])
                  }
                }}
                pointer={true}
                onClick={(e, map) => {
                  const feature = e.features[0]
                  console.log(feature)
                  router
                    .push(
                      `/map/dark-downtown/${feature.properties.slug}`,
                      undefined,
                      { shallow: true },
                    )
                    .then(() => {
                      console.log(feature)
                      map.setPaintProperty('artworks', 'text-opacity', [
                        'match',
                        ['get', 'slug'],
                        feature.properties.slug,
                        0,
                        1,
                      ])
                      // this will work but we need to split layers
                      map.setLayoutProperty('artworks', 'icon-image', [
                        'match',
                        ['get', 'slug'],
                        feature.properties.slug,
                        `${iconPrefix}active`,
                        `${iconPrefix}base`,
                      ])
                    })
                }}
              />
              <Mapbox.Layer
                layerName="icons"
                visible={slug === 'dark-downtown'}
              />
              <Mapbox.Layer
                layerName="label"
                visible={slug !== 'dark-downtown' && mapState === 'dark-mofo'}
              />
              {slug === 'dark-downtown' && (
                <Mapbox.Zoom
                  zoom={ddtZoom}
                  coords={['147.3233942060232', '-42.88212827686018']}
                >
                  <BackToMap />
                  {slugSplit.length > 1 && (
                    <Mapbox.Modal
                      location={
                        events.filter(
                          event => event.slug.current === slugSplit[1],
                        )[0]
                      }
                      onClose={map => {
                        // close and reset layer styles
                        router.push(`/map/dark-downtown`, undefined, {
                          shallow: true,
                        })
                        map.setLayoutProperty(
                          'artworks',
                          'icon-image',
                          `${iconPrefix}base`,
                        )
                        map.setPaintProperty('artworks', 'text-opacity', 1)
                      }}
                    />
                  )}
                </Mapbox.Zoom>
              )}
              {/* All others */}
              {slug != 'dark-downtown' && slug != 'hobart-waterfront' && (
                <Mapbox.Zoom
                  zoom={darkMapZoom}
                  coords={['147.32783768176523', '-42.88150606123612']}
                >
                  {darkMapLocations.map((location, i) => {
                    const isCurrent =
                      locationForSlug?.slug &&
                      locationForSlug.slug === location.slug
                    return (
                      <Mapbox.Marker
                        location={location}
                        key={location.slug || i}
                        active={isCurrent}
                      />
                    )
                  })}
                </Mapbox.Zoom>
              )}
            </Mapbox.Layer>
          ) : (
            <Mapbox.Zoom
              zoom={ttZoom}
              coords={['146.67417438707264', '-42.160848810219946']}
            >
              {ttLocations.map((location, i) => {
                const isCurrent =
                  locationForSlug?.slug &&
                  locationForSlug.slug === location.slug

                return (
                  <Mapbox.Marker
                    location={location}
                    key={location.slug || i}
                    active={isCurrent}
                    options={{
                      disableZoom: true,
                      disableCenter: true,
                    }}
                  />
                )
              })}
            </Mapbox.Zoom>
          )}
          <Mapbox.Controls>
            <Mapbox.ZoomControl type="plus" />
            <Mapbox.ZoomControl type="minus" />
            <Mapbox.PitchControl />
            <Mapbox.LocationControl />
          </Mapbox.Controls>
        </Mapbox>
      </Flex>
    </PageLayout>
  )
}

const BackToMap = () => (
  <Link href="/map" scroll={false} passHref>
    <Text
      as="a"
      sx={{
        position: 'absolute',
        variant: `buttons.primaryReverse`,
        backgroundColor: 'black',
        left: '12px',
        top: '12px',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        // width="13.089"
        // height="8.517"
        viewBox="0 0 13.089 8.517"
        sx={{
          width: '0.71em',
          // ['5px', null, '6px', '7px', 'auto'],
          stroke: 'red',
          transform: 'rotate(-90deg)',
          mr: '0.5em',
          ml: '-0.1em',
        }}
      >
        <path
          id="Path_30"
          data-name="Path 30"
          d="M0,0,5.6,6.736l.564.679.564-.679L12.32,0"
          transform="translate(12.705 8.198) rotate(180)"
          fill="none"
          strokeWidth="2"
        />
      </svg>{' '}
      Full Map
    </Text>
  </Link>
)

export async function getStaticProps({ params, preview = false }) {
  const { ttLocations } = await getClient(preview).fetch(ttLocationsQuery)
  return {
    props: { data: { ttLocations }, preview },
    revalidate: 365 * 24 * 60 * 60,
  }
}

export async function getStaticPaths() {
  const {
    home: { events },
  } = useGlobalStaticData()

  const paths = ['/map']

  events.forEach(event => {
    event?.locations?.forEach(eventLocation => {
      if (!eventLocation.hideOnMap) {
        paths.push({ params: { slug: [eventLocation.slug] } })
      }
    })
  })

  ddtSlugs.forEach(slug => {
    paths.push({ params: { slug: ['dark-downtown', slug] } })
  })

  vpSlugs.forEach(slug => {
    paths.push({ params: { slug: ['hobart-waterfront', slug] } })
  })

  const { ttLocations } = await getClient().fetch(ttLocationsQuery)

  ttLocations.forEach(location => {
    paths.push({ params: { slug: [location.slug] } })
  })

  paths.push({ params: { slug: ['off-season'] } })

  return {
    paths,
    fallback: false,
    // fallback: true, // Generate on first load
  }
}
